import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { toast } from "sonner";
import { UserPlus, Trash2, Shield, User as UserIcon } from "lucide-react";

export default function Admin() {
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({ email: "", name: "", password: "", role: "user" });

  const load = async () => {
    try {
      const r = await api.get("/admin/users");
      setUsers(r.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erreur");
    }
  };

  useEffect(() => { load(); }, []);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const create = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/auth/register", form);
      toast.success(`Utilisateur ${form.email} créé`);
      setForm({ email: "", name: "", password: "", role: "user" });
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erreur");
    } finally { setLoading(false); }
  };

  const remove = async (u) => {
    if (u.id === user?.id) {
      toast.error("Impossible de supprimer votre propre compte.");
      return;
    }
    if (!window.confirm(`Supprimer ${u.email} ? Toutes ses données (comptes email, filtres, emails) seront effacées.`)) return;
    try {
      await api.delete(`/admin/users/${u.id}`);
      toast.success("Utilisateur supprimé");
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erreur");
    }
  };

  return (
    <div className="p-6 md:p-8" data-testid="admin-page">
      <div className="flex items-center justify-between mb-8">
        <div>
          <div className="overline mb-1">// Administration</div>
          <h1 className="text-3xl font-bold tracking-tight mb-2" style={{fontFamily:"'Work Sans', sans-serif"}}>
            Gestion des utilisateurs
          </h1>
          <p className="text-gray-600 text-sm flex items-center gap-2">
            <Shield className="w-4 h-4 text-[#002FA7]" />
            Vous êtes connecté en tant qu'administrateur. Vous pouvez créer et supprimer des comptes.
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Create user form */}
        <div className="bg-white border border-gray-200 p-6">
          <div className="flex items-center gap-2 mb-1">
            <UserPlus className="w-4 h-4 text-[#002FA7]" />
            <div className="overline">// Nouveau</div>
          </div>
          <h3 className="text-lg font-semibold mb-4" style={{fontFamily:"'Work Sans', sans-serif"}}>
            Créer un utilisateur
          </h3>
          <form onSubmit={create} className="space-y-4" data-testid="admin-create-form">
            <div>
              <label className="overline block mb-2">Nom</label>
              <input value={form.name} onChange={(e)=>update("name", e.target.value)}
                data-testid="admin-name-input" required
                className="w-full h-11 px-3 border border-gray-300 focus:border-[#002FA7] outline-none text-sm" />
            </div>
            <div>
              <label className="overline block mb-2">Email</label>
              <input type="email" value={form.email} onChange={(e)=>update("email", e.target.value)}
                data-testid="admin-email-input" required
                placeholder="user@entreprise.com"
                className="w-full h-11 px-3 border border-gray-300 focus:border-[#002FA7] outline-none text-sm" />
            </div>
            <div>
              <label className="overline block mb-2">Mot de passe initial</label>
              <input type="text" value={form.password} onChange={(e)=>update("password", e.target.value)}
                data-testid="admin-password-input" required minLength={6}
                placeholder="Min. 6 caractères"
                className="w-full h-11 px-3 border border-gray-300 focus:border-[#002FA7] outline-none text-sm font-mono" />
              <div className="text-xs text-gray-500 mt-1">À communiquer à l'utilisateur, qui pourra le changer plus tard.</div>
            </div>
            <div>
              <label className="overline block mb-2">Rôle</label>
              <div className="grid grid-cols-2 border border-gray-300">
                <button type="button" onClick={() => update("role", "user")}
                  data-testid="admin-role-user"
                  className={`flex items-center justify-center gap-2 py-3 text-xs uppercase tracking-wider transition-colors ${
                    form.role === "user" ? "bg-[#002FA7] text-white" : "bg-white text-gray-700 hover:bg-gray-50"
                  }`}>
                  <UserIcon className="w-4 h-4" /> Utilisateur
                </button>
                <button type="button" onClick={() => update("role", "admin")}
                  data-testid="admin-role-admin"
                  className={`flex items-center justify-center gap-2 py-3 text-xs uppercase tracking-wider transition-colors ${
                    form.role === "admin" ? "bg-[#FF3B30] text-white" : "bg-white text-gray-700 hover:bg-gray-50"
                  }`}>
                  <Shield className="w-4 h-4" /> Administrateur
                </button>
              </div>
            </div>
            <button type="submit" disabled={loading} data-testid="admin-create-btn"
              className="w-full h-11 bg-[#002FA7] hover:bg-[#002277] text-white font-medium transition-colors disabled:opacity-50 flex items-center justify-center gap-2">
              <UserPlus className="w-4 h-4" /> {loading ? "Création…" : "Créer le compte"}
            </button>
          </form>
        </div>

        {/* Users list */}
        <div className="lg:col-span-2 bg-white border border-gray-200">
          <div className="p-6 border-b border-gray-200">
            <div className="overline mb-1">// Comptes actifs ({users.length})</div>
            <h3 className="text-lg font-semibold" style={{fontFamily:"'Work Sans', sans-serif"}}>
              Tous les utilisateurs
            </h3>
          </div>
          {users.length === 0 ? (
            <div className="p-10 text-center text-gray-500 text-sm">Aucun utilisateur.</div>
          ) : (
            <ul className="divide-y divide-gray-200" data-testid="admin-users-list">
              {users.map((u) => (
                <li key={u.id} className="p-4 flex items-center gap-4" data-testid={`admin-user-row-${u.id}`}>
                  <div className={`w-10 h-10 flex items-center justify-center text-sm font-semibold text-white shrink-0 ${
                    u.role === "admin" ? "bg-[#FF3B30]" : "bg-[#002FA7]"
                  }`}>
                    {(u.name || "?").charAt(0).toUpperCase()}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <div className="font-medium text-gray-900 truncate">{u.name}</div>
                      <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider border ${
                        u.role === "admin"
                          ? "bg-red-50 text-red-700 border-red-200"
                          : "bg-blue-50 text-[#002FA7] border-blue-200"
                      }`}>
                        {u.role === "admin" && <Shield className="w-2.5 h-2.5" />}
                        {u.role}
                      </span>
                      {u.id === user?.id && (
                        <span className="text-[10px] uppercase tracking-wider text-gray-500">(vous)</span>
                      )}
                    </div>
                    <div className="text-xs text-gray-500 truncate">{u.email}</div>
                    {u.created_at && (
                      <div className="text-xs text-gray-400 font-mono mt-0.5">
                        Créé le {new Date(u.created_at).toLocaleDateString("fr-FR")}
                      </div>
                    )}
                  </div>
                  <button onClick={() => remove(u)} disabled={u.id === user?.id}
                    data-testid={`admin-delete-${u.id}`}
                    title={u.id === user?.id ? "Vous ne pouvez pas vous supprimer" : "Supprimer"}
                    className="p-2 text-red-700 hover:bg-red-50 border border-red-200 hover:border-red-700 transition-colors disabled:opacity-30 disabled:cursor-not-allowed">
                    <Trash2 className="w-4 h-4" />
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}
